import { scenePropParts } from './sceneProps.js';
import { RAD, clamp } from './constants.js';
import { isPoleObject, poleParts } from './poles.js';

// Dimensions are editable reference sizes, not a population-wide average.
export const PERSON_DEFAULTS = { kind: 'person', height: 1.75, pose: 'standing', seatHeight: 0.45, pan: 0 };
export const FURNITURE_DEFAULTS = {
  desk: { kind: 'desk', width: 1.4, depth: 0.7, height: 0.75 },
  chair: { kind: 'chair', width: 0.5, depth: 0.5, height: 0.9, seatHeight: 0.45 }
};
export const presentInScene = (o, state = 'design') => {
  state = o._groundSceneState ?? state;
  return o.enabled !== false && !(o.scenario === 'occupied' && state !== 'occupied') && !(o.scenario === 'clear' && state === 'occupied');
};
export const chairSeatHeight = o => {
  const t = Math.min(0.045, o.height * 0.08);
  return clamp(o.seatHeight ?? 0.45, t * 2, o.height - t);
};

// Local X follows heading; Z is height. These parts feed 2D, analytical rays
// and walking. Furniture uses them in 3D too; vehicles use a detailed visual
// mesh with these simplified obstruction volumes retained for analysis.
export function obstacleParts(o) {
  if (isPoleObject(o)) return poleParts(o);
  const detailed = scenePropParts(o); if (detailed) return detailed;
  const w = o.width, d = o.depth, h = o.height;
  const box = (role, x, y, z, width, depth, height, color) => ({ role, x, y, z, width, depth, height, color });
  if (o.kind === 'vehicle') {
    const parts = [box('body', 0, 0, h * 0.38, w, d * 0.94, h * 0.46, '#426781'),
      box('cabin', -w * 0.07, 0, h * 0.79, w * 0.48, d * 0.8, h * 0.42, '#34495a')];
    for (const x of [-1, 1]) for (const y of [-1, 1]) parts.push(box('wheel', x * w * 0.32, y * d * 0.44, h * 0.17, w * 0.13, d * 0.12, h * 0.34, '#22282d'));
    return parts;
  }
  if (!FURNITURE_DEFAULTS[o.kind]) return [box('body', 0, 0, h / 2, w, d, h)];
  const t = Math.min(0.045, h * 0.08), leg = Math.min(0.045, w * 0.12, d * 0.12);
  const seat = o.kind === 'chair' ? chairSeatHeight(o) : h;
  const parts = [box(o.kind === 'desk' ? 'top' : 'seat', 0, 0, seat - t / 2, w, d, t, o.kind === 'desk' ? '#cba577' : '#4c8296')];
  for (const x of [-1, 1]) for (const y of [-1, 1]) parts.push(box('leg', x * (w - leg) / 2, y * (d - leg) / 2, (seat - t) / 2, leg, leg, seat - t, '#56616e'));
  if (o.kind === 'chair') parts.push(box('back', -w / 2 + t / 2, 0, (h + seat) / 2, t, d, h - seat, '#4c8296'));
  return parts;
}

export function personDimensions(S, target) {
  // Older face targets retain their configured face elevation until resized.
  const height = target.height ?? (S.opt.hFace * 1.75 / 1.6);
  const seatHeight = target.seatHeight ?? 0.45, seated = target.pose === 'seated';
  const drop = seated ? height * 0.465 - seatHeight : 0;
  return { height, seatHeight, seated, drop, faceHeight: height * (1.6 / 1.75) - drop,
    faceOffset: height * 0.08, faceWidth: height * (0.16 / 1.75), faceSize: height * (0.22 / 1.75),
    top: height - drop };
}

export function personParts(S, target) {
  const { height: h, seated, drop, seatHeight, faceHeight, faceOffset, faceWidth, faceSize } = personDimensions(S, target);
  const parts = [];
  const add = (role, shape, x, y, z, width, depth, height, color) => parts.push({ role, shape, x, y, z, width, depth, height, color });
  const skin = '#d9aa82', shirt = '#407a98', trousers = '#374558';
  add('torso', 'ellipsoid', 0, 0, h * 0.68 - drop, h * 0.18, h * 0.27, h * 0.34, shirt);
  add('hips', 'ellipsoid', 0, 0, h * 0.52 - drop, h * 0.17, h * 0.22, h * 0.13, trousers);
  add('neck', 'ellipsoid', 0, 0, h * 0.83 - drop, h * 0.07, h * 0.08, h * 0.08, skin);
  add('head', 'ellipsoid', 0, 0, faceHeight, h * 0.16, h * 0.115, 2 * (h - (faceHeight + drop)), skin);
  add('face', 'box', faceOffset, 0, faceHeight, 0.008, faceWidth, faceSize, '#e9c4a0');
  for (const side of [-1, 1]) {
    add('eye', 'ellipsoid', faceOffset + 0.008, side * faceWidth * 0.23, faceHeight + faceSize * 0.15, 0.012, h * 0.012, h * 0.01, '#263442');
    add('arm', 'ellipsoid', 0, side * h * 0.16, h * 0.68 - drop, h * 0.07, h * 0.075, h * 0.28, shirt);
    add('hand', 'ellipsoid', 0, side * h * 0.16, h * 0.52 - drop, h * 0.045, h * 0.05, h * 0.08, skin);
    if (seated) {
      add('thigh', 'ellipsoid', h * 0.11, side * h * 0.065, seatHeight + h * 0.055, h * 0.32, h * 0.095, h * 0.11, trousers);
      const foot = Math.min(h * 0.055, seatHeight * 0.3);
      add('shin', 'ellipsoid', h * 0.24, side * h * 0.065, (seatHeight + foot) / 2, h * 0.085, h * 0.08, seatHeight - foot, trousers);
      add('shoe', 'box', h * 0.27, side * h * 0.065, foot / 2, h * 0.15, h * 0.09, foot, '#25303d');
    } else {
      add('leg', 'ellipsoid', 0, side * h * 0.065, h * 0.27, h * 0.11, h * 0.1, h * 0.47, trousers);
      add('shoe', 'box', h * 0.035, side * h * 0.065, h * 0.0275, h * 0.15, h * 0.09, h * 0.055, '#25303d');
    }
  }
  return parts;
}

export function targetWorldPoint(target, x, y, height) {
  const a = (target.pan || 0) * RAD;
  return { x: target.x + Math.cos(a) * x - Math.sin(a) * y, y: target.y + Math.sin(a) * x + Math.cos(a) * y, height };
}
